import React, { useEffect, useState, type ReactNode } from 'react';
import moment from 'moment';
import { Icon, Text } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { useTranslation } from 'Components/Helpers/translate';
import logError from 'Helpers/logError';
import { withMonthHeaders } from 'App/components/HistoryLog/HistoryLogMonthHeader/HistoryLogMonthHeader';
import type PublicChatbotConversation from 'App/resources/Model/PublicChatbotConversation';
import WebsiteChatOutcome from './WebsiteChatOutcome';
import { openWebsiteChat } from './openWebsiteChat';
import { personLabel } from './conversationDisplay';
import { loadWebsiteChatEvents, withWebsiteChats, WEBSITE_CHAT_ENTITY, type WebsiteChatEvent } from './timelineChats';
import './WebsiteChat.scss';

/** The handed-over website chats of one company or contact, for its React card timeline (WEB-12989). */
export const useLinkedWebsiteChats = (link: { clientId?: number; contactId?: number }) => {
	const [chats, setChats] = useState<WebsiteChatEvent[]>([]);
	const { clientId, contactId } = link;

	useEffect(() => {
		setChats([]);
		let alive = true;
		loadWebsiteChatEvents({ clientId, contactId })
			.then(rows => {
				if (alive) setChats(rows);
			})
			.catch(err => logError(err, 'Failed to load the website chats for the timeline'));
		return () => {
			alive = false;
		};
	}, [clientId, contactId]);

	return chats;
};

/**
 * One chat on the timeline: "Website chat with <visitor>", the opening
 * message and the hand-over badge. The title opens the chat's record view
 * on top (WEB-13681); the badge opens the to-do or ticket.
 */
export const WebsiteChatTimelineEntry = ({
	conversation,
	hideIcon = false
}: {
	conversation: PublicChatbotConversation;
	/** The Angular cards draw the icon in their own column. */
	hideIcon?: boolean;
}) => {
	const classes = new BemClass('WebsiteChatTimelineEntry');
	const { t } = useTranslation();

	return (
		<div className={classes.mod({ 'no-icon': hideIcon }).b()} data-testid="website-chat-timeline-entry">
			{hideIcon ? null : (
				<div className={classes.elem('icon').b()} aria-hidden="true">
					<Icon name="comments" />
				</div>
			)}
			<div className={classes.elem('main').b()}>
				<button
					type="button"
					className={classes.elem('title').b()}
					onClick={() => openWebsiteChat(conversation.id)}
					data-testid="website-chat-timeline-title"
				>
					{t('publicChatbot.chat.timelineTitle', { name: personLabel(conversation, t) })}
				</button>
				{conversation.firstVisitorMessage ? (
					<Text size="sm" color="grey-10" className={classes.elem('first').b()}>
						{`“${conversation.firstVisitorMessage}”`}
					</Text>
				) : null}
				<div className={classes.elem('badge').b()}>
					<WebsiteChatOutcome conversation={conversation} />
				</div>
			</div>
			<Text size="sm" color="grey-10" className={classes.elem('date').b()}>
				{moment(conversation.regDate).format('L')}
			</Text>
		</div>
	);
};

type DateLike = Date | string | null;

/** The timeline's events with the website chats sorted in among them by date, month headers and all. */
export const timelineWithWebsiteChats = <E extends { id?: number | string | null; date?: DateLike }>(
	events: E[],
	chats: WebsiteChatEvent[],
	hasMore: boolean,
	renderEvent: (event: E) => ReactNode
): ReactNode[] =>
	withMonthHeaders(withWebsiteChats(events, chats, hasMore), entry =>
		'chat' in entry && (entry as WebsiteChatEvent).entityType === WEBSITE_CHAT_ENTITY ? (
			<WebsiteChatTimelineEntry key={entry.id} conversation={(entry as WebsiteChatEvent).chat} />
		) : (
			<div key={(entry as E).id ?? undefined}>{renderEvent(entry as E)}</div>
		)
	);
